import { c as _c } from "react-compiler-runtime";
import { createContext, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldIds } from "@/components/form";
import { Text } from "@/components/primitives";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/core/utils";
type RadioOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};
type RadioGroupFieldContextValue = {
  groupId: string;
  labelId: string;
  hintId: string;
  errorId: string;
  firstItemId: string | null;
};
const RadioGroupFieldContext = createContext<RadioGroupFieldContextValue | null>(null);
function useRadioGroupFieldContext() {
  const ctx = useContext(RadioGroupFieldContext);
  if (!ctx) {
    throw new globalThis.Error("RadioGroupField components must be used within RadioGroupField.Root");
  }
  return ctx;
}
function getOptionId(groupId: string, value: string) {
  const safe = value.trim().replaceAll(/\s+/g, "-");
  return `${groupId}-option-${safe || "empty"}`;
}
type RadioGroupLabelProps = {
  text: string;
  invalid?: boolean;
};
function RadioGroupLabel(t0) {
  const $ = _c(5);
  const {
    text,
    invalid
  } = t0;
  const {
    firstItemId,
    labelId
  } = useRadioGroupFieldContext();
  const t1 = firstItemId ?? labelId;
  let t2;
  if ($[0] !== invalid || $[1] !== labelId || $[2] !== t1 || $[3] !== text) {
    t2 = <Label htmlFor={t1} id={labelId} invalid={invalid} text={text} />;
    $[0] = invalid;
    $[1] = labelId;
    $[2] = t1;
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type RadioGroupHintProps = {
  text: string;
};
function RadioGroupHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useRadioGroupFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type RadioGroupErrorMessageProps = {
  text: string;
};
function RadioGroupErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    groupId,
    errorId
  } = useRadioGroupFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== groupId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={groupId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = groupId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
  options: readonly RadioOption[];
};
function Root(t0) {
  const $ = _c(15);
  const {
    children,
    name,
    options
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.inputId || $[1] !== options[0] || $[2] !== options.length) {
    t1 = options.length > 0 ? getOptionId(fieldIds.inputId, options[0].value) : null;
    $[0] = fieldIds.inputId;
    $[1] = options[0];
    $[2] = options.length;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const firstItemId = t1;
  let t2;
  if ($[4] !== fieldIds.errorId || $[5] !== fieldIds.hintId || $[6] !== fieldIds.inputId || $[7] !== fieldIds.labelId || $[8] !== firstItemId) {
    t2 = {
      groupId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId,
      firstItemId
    };
    $[4] = fieldIds.errorId;
    $[5] = fieldIds.hintId;
    $[6] = fieldIds.inputId;
    $[7] = fieldIds.labelId;
    $[8] = firstItemId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const value = t2;
  let t3;
  if ($[10] !== children) {
    t3 = <div className="flex flex-col gap-1 text-left">{children}</div>;
    $[10] = children;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3 || $[13] !== value) {
    t4 = <RadioGroupFieldContext.Provider value={value}>{t3}</RadioGroupFieldContext.Provider>;
    $[12] = t3;
    $[13] = value;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
type NativeProps = {
  name: string;
  label: string;
  hint?: string;
  options: readonly RadioOption[];
  radioGroupProps?: Omit<React.ComponentPropsWithoutRef<typeof RadioGroup>, "id" | "name" | "value" | "defaultValue" | "onValueChange" | "children">;
  itemClassName?: string;
};
function Native(t0) {
  const $ = _c(14);
  const {
    name,
    label,
    hint,
    options,
    radioGroupProps,
    itemClassName
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <RadioGroupLabel text={label} />;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== itemClassName || $[3] !== options || $[4] !== radioGroupProps) {
    t2 = <Group options={options} {...radioGroupProps} itemClassName={itemClassName} />;
    $[2] = itemClassName;
    $[3] = options;
    $[4] = radioGroupProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== hint) {
    t3 = hint && <RadioGroupHint text={hint} />;
    $[6] = hint;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== name || $[9] !== options || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <Root name={name} options={options}>{t1}{t2}{t3}</Root>;
    $[8] = name;
    $[9] = options;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
type GroupProps = Omit<React.ComponentPropsWithoutRef<typeof RadioGroup>, "children"> & {
  options: readonly RadioOption[];
  itemClassName?: string;
  invalid?: boolean;
};
function Group(t0) {
  const $ = _c(25);
  let className;
  let invalid;
  let itemClassName;
  let options;
  let props;
  if ($[0] !== t0) {
    ({
      options,
      className,
      itemClassName,
      invalid,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = invalid;
    $[3] = itemClassName;
    $[4] = options;
    $[5] = props;
  } else {
    className = $[1];
    invalid = $[2];
    itemClassName = $[3];
    options = $[4];
    props = $[5];
  }
  const {
    errorId,
    groupId,
    hintId,
    labelId
  } = useRadioGroupFieldContext();
  const t1 = `${hintId} ${errorId}`;
  const t2 = invalid || undefined;
  let t3;
  if ($[6] !== className) {
    t3 = cn("mt-2", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== groupId || $[9] !== invalid || $[10] !== itemClassName || $[11] !== options) {
    let t5;
    if ($[13] !== groupId || $[14] !== invalid || $[15] !== itemClassName) {
      t5 = opt => {
        const id = getOptionId(groupId, opt.value);
        return <div className="flex items-start gap-2" key={opt.value}><RadioGroupItem className={cn(invalid && "border-red-600 text-red-600", itemClassName)} disabled={opt.disabled} id={id} value={opt.value} /><label className={cn("grid gap-0.5 text-sm leading-none", opt.disabled && "cursor-not-allowed opacity-50")} htmlFor={id}><Text component="span" text={opt.label} tone="foreground" variant="body-sm" weight="medium" />{opt.description && <Text component="span" text={opt.description} tone="muted" variant="caption" />}</label></div>;
      };
      $[13] = groupId;
      $[14] = invalid;
      $[15] = itemClassName;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t4 = options.map(t5);
    $[8] = groupId;
    $[9] = invalid;
    $[10] = itemClassName;
    $[11] = options;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[17] !== groupId || $[18] !== labelId || $[19] !== props || $[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t5 = <RadioGroup {...props} aria-describedby={t1} aria-invalid={t2} aria-labelledby={labelId} className={t3} id={groupId}>{t4}</RadioGroup>;
    $[17] = groupId;
    $[18] = labelId;
    $[19] = props;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}
type RHF<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  hint?: string;
  options: readonly RadioOption[];
  radioGroupProps?: Omit<React.ComponentPropsWithoutRef<typeof RadioGroup>, "id" | "name" | "value" | "defaultValue" | "onValueChange" | "children">;
  itemClassName?: string;
};
function RHF(t0) {
  const $ = _c(27);
  const {
    name,
    label,
    hint,
    options,
    radioGroupProps,
    itemClassName
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = {
      name
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    field,
    fieldState
  } = useController(t1);
  const errorText = fieldState.error?.message;
  const t2 = !!errorText;
  let t3;
  if ($[2] !== label || $[3] !== t2) {
    t3 = <RadioGroupLabel invalid={t2} text={label} />;
    $[2] = label;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = !!errorText;
  const t5 = typeof field.value === "string" ? field.value : field.value ?? "";
  let t6;
  if ($[5] !== field.name || $[6] !== field.onBlur || $[7] !== field.onChange || $[8] !== itemClassName || $[9] !== options || $[10] !== radioGroupProps || $[11] !== t4 || $[12] !== t5) {
    t6 = <Group {...radioGroupProps} invalid={t4} itemClassName={itemClassName} name={field.name} options={options} value={t5} onBlur={field.onBlur} onValueChange={field.onChange} />;
    $[5] = field.name;
    $[6] = field.onBlur;
    $[7] = field.onChange;
    $[8] = itemClassName;
    $[9] = options;
    $[10] = radioGroupProps;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== hint) {
    t7 = hint && <RadioGroupHint text={hint} />;
    $[14] = hint;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== errorText) {
    t8 = errorText && <RadioGroupErrorMessage text={errorText} />;
    $[16] = errorText;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="h-2">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== name || $[21] !== options || $[22] !== t3 || $[23] !== t6 || $[24] !== t7 || $[25] !== t9) {
    t10 = <Root name={name} options={options}>{t3}{t6}{t7}{t9}</Root>;
    $[20] = name;
    $[21] = options;
    $[22] = t3;
    $[23] = t6;
    $[24] = t7;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
export const RadioGroupField = {
  RHF,
  Native
};